import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { ApolloClient, InMemoryCache } from "@apollo/client";
import dayjs from "dayjs";
import Posts from "../../components/Posts/Posts";
import { GET_ALL_POSTS, GET_ALL_TAGS } from "../../graphql/posts";
import Pagination from "../../components/Pagination/Pagination";
import { CloseIcon } from "../../assets";
import PostsRow from "../../components/PostsRow/PostsRow";
import Head from "next/head";
import NavHook from "../../components/Nav/NavHook";
import Navbar from "../../components/Nav/Nav";
import Footer from "../../components/Footer/Footer";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import RefreshedFooter from "../../components/RefreshedFooter/RefreshedFooter";
import BlogSearchBar from "../../components/BlogSearchBar/BlogSearchBar";

export default function Blog({ posts, tags, footer, navigation }) {
  const [filters, setFilters] = useState([]);
  const filtersParams = filters.map((f) => f.name);
  const [locale, setLocale] = useState("");
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  const router = useRouter();
  useEffect(() => {
    router.push({
      pathname: "/blog",
      query: {
        page: router.query.page ? router.query.page : 1,
        search: searchTerm ? searchTerm : [],
        filters: filtersParams,
      },
    });
  }, [filters, searchTerm]);

  useEffect(() => {
    const topLevelDomain = window.location.hostname.split(".").pop();
    setLocale(topLevelDomain === "es" ? "es" : "fr");
  }, []);

  useEffect(() => {
    window?.rudderanalytics?.page();
  }, []);

  return (
    <>
      <Head>
        <title>Bene Bono</title>
        <meta
          property="og:title"
          content="Bene Bono | Cestas Ecológicas y Anti Desperdicio"
        />
        <meta
          property="og:site_name"
          content="Bene Bono"
        />
        <meta
          property="og:url"
          content={`benebono.${locale}`}
        />
        <meta
          property="og:description"
          content=""
        />
        <meta
          property="og:type"
          content="website"
        />
        <meta
          property="og:image"
          content="https://img.plasmic.app/img-optimizer/v1/img/c44d43862fdbf580d7139ef62c2c6ffa.png?q=75&f=webp"
        />
        {process.env.NODE_ENV !== "production" && (
          <meta
            name="robots"
            content="index,follow"
          />
        )}
      </Head>
      <Navbar links={navigation.links} />
      <div className="articles_container">
        <h1>{t("blog.title")}</h1>
        <div className="articles">
        <BlogSearchBar
            setSearchTerm={setSearchTerm}
          />
          <div className="articles_section">
            {posts.data.map((p, i) => {
              const tag = p.attributes.tags.data.map((t) => t.attributes.name);
              if (i > 0 && i % 4 === 0) {
                return (
                  <PostsRow
                    key={i}
                    imageUrl={
                      p.attributes?.preview_image?.data[0]?.attributes?.url
                    }
                    title={p.attributes.title}
                    author={p.attributes.author}
                    date={dayjs(p.attributes.publishedAt).format(
                      "DD MMMM YYYY"
                    )}
                    tags={tag}
                    description={p.attributes.description}
                    slugUrl={p.attributes.slug}
                  />
                );
              }
              return (
                <Posts
                  key={i}
                  imageUrl={
                    p.attributes?.preview_image?.data[0]?.attributes?.url
                  }
                  title={p.attributes.title}
                  author={p.attributes.author}
                  date={dayjs(p.attributes.publishedAt).format("DD MMMM YYYY")}
                  tags={tag}
                  description={p.attributes.description}
                  slugUrl={p.attributes.slug}
                />
              );
            })}
            <Pagination
              pageCount={posts.meta.pagination.pageCount}
              link="/blog"
              filters={filtersParams}
            />
          </div>
          <div className="tags_container">
            <h3>{t("blog.category")}</h3>
            <ul>
              {tags.map((t) => {
                const isIncludes = filters.some((f) =>
                  f.name.includes(t.attributes.name)
                );
                return (
                  <li
                    style={
                      isIncludes
                        ? { backgroundColor: "#F06E36", color: "#FFFFFF" }
                        : {
                            backgroundColor: "rgba(240, 110, 54, 0.16)",
                            color: "#f06e36",
                          }
                    }
                    onClick={() => {
                      const name = t.attributes.name;
                      const isIncludes = filters.some((f) =>
                        f.name.includes(name)
                      );
                      if (!isIncludes)
                        setFilters((prevValue) => [
                          ...prevValue,
                          { name, isActive: true },
                        ]);

                      if (isIncludes) {
                        setFilters(filters.filter((f) => f.name !== name));
                      }
                    }}
                    key={t.id}
                  >
                    {t.attributes.name}
                    {isIncludes ? <CloseIcon /> : null}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
      <RefreshedFooter links={footer.links} />
    </>
  );
}

export async function getServerSideProps({ query, locale }) {
  const client = new ApolloClient({
    uri: process.env.STRAPI_GRAPHQL_URL,
    cache: new InMemoryCache(),
  });

  const post = await client.query({
    query: GET_ALL_POSTS,
    variables: {
      page: query.page ? parseInt(query.page) : 1,
      search: query.search ? query.search : "",
      pageSize: 10,
      tags: query.filters ? query.filters : undefined,
      locale: locale === "es" ? locale : "fr-FR",
    },
  });

  const tag = await client.query({
    query: GET_ALL_TAGS,
    variables: {
      locale: locale === "es" ? locale : "fr-FR",
    },
  });

  const mainNav = await NavHook(locale, "main");
  const navigation = {
    links: mainNav,
  };

  const footerNav = await NavHook(locale, "footer");
  const footer = {
    links: footerNav,
  };

  return {
    props: {
      ...(await serverSideTranslations(locale)),
      posts: post.data.blogPosts,
      tags: tag.data.tags.data,
      footer,
      navigation,
      locale,
    },
  };
}
